import { Link, useLocation } from 'react-router-dom';
import { HelpPanel, type HelpContent, type HelpIndex } from '@babomrs/ui';
import index from '../help/index.json';

/**
 * Client-portal contextual help — frontend-ui-system.md § In-app help system (binding:
 * "Every portal must include a built-in contextual help system").
 *
 * The panel itself is shared (@babomrs/ui); only the CONTENT is portal-specific, which
 * the rule requires: "Admin Portal and Client Portal have separate help content —
 * different audiences, different language, different features visible." Sharing the
 * resolution and rendering means the two portals cannot drift on how a route maps to
 * its help; keeping the content separate means they cannot drift into one voice.
 */

/**
 * Loaded by glob, NOT a hand-written import list.
 *
 * The list this replaces named all nine content files by hand — the same shape as the
 * defects this closed out: the portal home missing a Documents link (#16982) and
 * AdminNav missing two tabs (#16988), both a hand-maintained list diverging from what
 * it mirrored. `eager: true` keeps it a build-time import (bundled and tree-shaken, no
 * runtime fetch) while making the file system the single source of truth: dropping a
 * JSON file into src/help/ registers it, with no second place to forget.
 */
const modules = import.meta.glob<{ default: HelpContent }>('../help/*.json', { eager: true });

const CONTENT: Record<string, HelpContent> = Object.fromEntries(
  Object.entries(modules)
    .map(([path, mod]) => [path.split('/').pop() as string, mod.default])
    // index.json is the route map, not content — exclude it or it renders as a topic.
    .filter(([name]) => name !== 'index.json'),
);

/**
 * Re-exported so the Help Centre page (#10803 AC5) reads the SAME glob and the SAME
 * index as the contextual panel, rather than introducing a second source of truth for
 * "what help exists".
 */
export const CLIENT_HELP_CONTENT = CONTENT;
export const CLIENT_HELP_INDEX = index as HelpIndex;

export function ClientHelpPanel({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { pathname } = useLocation();

  return (
    <HelpPanel
      open={open}
      onClose={onClose}
      index={index as HelpIndex}
      content={CONTENT}
      pathname={pathname}
      emptyMessage="There is no help for this page yet. Raise a query and we will help you directly."
      emptyAction={
        <Link
          to="/portal/help"
          onClick={onClose}
          className="text-sm font-medium text-[var(--accent)] hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent)] rounded"
        >
          Search all help topics
        </Link>
      }
    />
  );
}
